import React from "react";

import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/storage";

import {
  Box,
  Typography,
  Grid,
  ImageListItem,
  ImageListItemBar,
  Button,
  Paper,
  Chip,
  Divider,
  Card,
  CardContent,
  useTheme,
  alpha,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useParams } from "react-router-dom";
import Bugsnag from "@bugsnag/js";
import ReactPlayer from "react-player";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import DeleteIcon from "@mui/icons-material/Delete";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import VideocamIcon from "@mui/icons-material/Videocam";
import DescriptionIcon from "@mui/icons-material/Description";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import AddVideoDialog from "./AddVideoDialog";
import ExerciseDialog from "./ExerciseDialog";
import GlobalData from "../../../../data/GlobalData";
import NewLineTypography from "../../../../atoms/NewLineTypography";
import { consola } from "consola";
import NewColors from "../../../../themes/NewColors";
import useEvents from "../../../../hooks/useEvents";
import {
  useExerciseBigQuery,
  useUpdateExerciseBigQuery,
} from "../../../../hooks/useExercisesBigQuery";
import {
  buildExerciseCatalogUpdatePayload,
  removeVideoFromList,
} from "../../../../utils/exerciseCatalogPayload";

const ExerciseDetails = () => {
  const theme = useTheme();
  const confirm = useConfirm();
  const { exerciseId, orgId } = useParams();
  // const { findExercise } = useProtocols();

  const [addVideoURLOpen, setAddVideoURLOpen] = React.useState(false);
  const [isAddingVideo, setIsAddingVideo] = React.useState(false);
  const [isDeletingVideo, setIsDeletingVideo] = React.useState({});
  const [isUploadingImage, setIsUploadingImage] = React.useState({});
  const [isDeletingImage, setIsDeletingImage] = React.useState({});
  const [exerciseDialogOpen, setExerciseDialogOpen] = React.useState(false);
  // const [foundProtocols, setFoundProtocols] = React.useState([]);

  // Use BigQuery hook to fetch exercise data
  const {
    data: exerciseResponse,
    isLoading,
    error,
  } = useExerciseBigQuery(exerciseId, !!exerciseId);
  const exercise = exerciseResponse?.data?.exercise || null;

  // Use BigQuery mutation for updating exercise data
  const updateExerciseMutation = useUpdateExerciseBigQuery();

  console.log(
    "🔵 ExerciseDetails - exerciseId:",
    exerciseId,
    "orgId:",
    orgId,
    "exercise:",
    exercise,
    "loading:",
    isLoading,
    "error:",
    error,
  );

  // Debug the exercise structure
  if (exercise) {
    console.log("🔍 Exercise data structure:", {
      name: exercise.name,
      mainMuscleGroups: exercise.mainMuscleGroups,
      secondaryMuscleGroups: exercise.secondaryMuscleGroups,
      equipment: exercise.equipment,
      difficulty: exercise.difficulty,
      images: exercise.images,
      videos: exercise.videos,
    });
  }

  const uidRef = React.useRef(localStorage.getItem("uid"));
  const events = useEvents();

  const buildUpdatePayload = React.useCallback(
    (overrides = {}) => buildExerciseCatalogUpdatePayload(exercise, overrides),
    [exercise],
  );

  // Helper function to add a video to the exercise
  const addVideoToExercise = async (videoURL) => {
    if (!exercise || !videoURL) return;

    setIsAddingVideo(true);
    try {
      const currentVideos = exercise.videos || [];
      const updatedVideos = [...currentVideos, videoURL.trim()];

      await updateExerciseMutation.mutateAsync({
        exerciseId: exercise.id,
        exerciseData: buildUpdatePayload({ videos: updatedVideos }),
      });
    } catch (saveError) {
      consola.error("Failed to add exercise video:", saveError);
      await confirm({
        title: "Could not save video",
        description:
          saveError?.message ||
          "The video link could not be saved. Please try again.",
        cancellationText: "",
        confirmationText: "OK",
      });
    } finally {
      setIsAddingVideo(false);
    }
  };

  // Helper function to delete a video from the exercise
  const deleteVideoFromExercise = async (videoURL) => {
    if (!exercise || !videoURL) return;

    setIsDeletingVideo((prev) => ({ ...prev, [videoURL]: true }));
    try {
      const updatedVideos = removeVideoFromList(exercise.videos, videoURL);
      if (updatedVideos.length === (exercise.videos || []).length) {
        throw new Error(
          "The video could not be matched for removal. Try refreshing the page and try again.",
        );
      }

      await updateExerciseMutation.mutateAsync({
        exerciseId: exercise.id,
        exerciseData: buildUpdatePayload({ videos: updatedVideos }),
      });
    } catch (saveError) {
      consola.error("Failed to delete exercise video:", saveError);
      await confirm({
        title: "Could not remove video",
        description:
          saveError?.message ||
          "The video could not be removed. Please try again.",
        cancellationText: "",
        confirmationText: "OK",
      });
    } finally {
      setIsDeletingVideo((prev) => ({ ...prev, [videoURL]: false }));
    }
  };

  // Helper function to update an image in the exercise
  const updateImageInExercise = async (imageURL, imageIndex) => {
    if (!exercise) return;

    if (imageURL === "") {
      // Deleting image
      setIsDeletingImage((prev) => ({ ...prev, [imageIndex]: true }));
    } else {
      // Adding/updating image
      setIsUploadingImage((prev) => ({ ...prev, [imageIndex]: true }));
    }

    try {
      const currentImages = exercise.images || [];
      const updatedImages = [...currentImages];

      // Ensure the array is at least as long as needed
      while (updatedImages.length <= imageIndex) {
        updatedImages.push("");
      }

      updatedImages[imageIndex] = imageURL;

      await updateExerciseMutation.mutateAsync({
        exerciseId: exercise.id,
        exerciseData: buildUpdatePayload({ images: updatedImages }),
      });
    } finally {
      setIsUploadingImage((prev) => ({ ...prev, [imageIndex]: false }));
      setIsDeletingImage((prev) => ({ ...prev, [imageIndex]: false }));
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6">Loading exercise...</Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6" color="error">
          Error loading exercise: {error.message}
        </Typography>
      </Box>
    );
  }

  // Show not found state
  if (!exercise) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6">Exercise not found</Typography>
      </Box>
    );
  }

  // REFACTOR: Remove the call to user data values.
  const handleCustomImageUpload = async (uid, file, imageIndex, exercise) => {
    const ref = firebase.firestore().collection("users").doc(uid);
    consola.debug("++++++ READ -> ExerciseDetails: handleCustomImageUpload");
    const userData = await ref.get();
    if (userData.exists) {
      const userDataValues = userData.data();

      const organizationId = userDataValues.organizationId;
      const path = `/${organizationId}/exercise_images/${exercise.id}_${imageIndex}`;
      const uploadTask = firebase.storage().ref(path).put(file);

      uploadTask.on(
        "state_changed",
        () => {
          // progress function ...
          // const progress = Math.round(
          //   (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          // );
        },
        (error) => {
          consola.error(new Error(error));
        },
        () => {
          firebase
            .storage()
            .ref(path)
            .getDownloadURL()
            .then(async (url) => {
              await updateImageInExercise(url, imageIndex);
            })
            .catch((e) => {
              Bugsnag.notify(e);
            });
        },
      );
    }
  };

  const handleCustomImageDelete = async (uid, imageIndex, exercise) => {
    const ref = firebase.firestore().collection("users").doc(uid);
    consola.debug("++++++ READ -> ExerciseDetails: handleCustomImageDelete");
    const userData = await ref.get();
    if (userData.exists) {
      const userDataValues = userData.data();
      events.logButtonClick({
        buttonName: "Custom Image Delete",
        pageName: "Exercises",
        tabName: exercise.id,
      });

      const organizationId = userDataValues.organizationId;
      const path = `/${organizationId}/exercise_images/${exercise.id}_${imageIndex}`;
      firebase
        .storage()
        .ref(path)
        .delete()
        .then(async () => {
          await updateImageInExercise("", imageIndex);
        });
    }
  };

  const handleImage1Upload = async (e) => {
    if (e.target.files[0]) {
      events.logButtonClick({
        buttonName: "Image 1 Upload",
        pageName: "Exercises",
        tabName: exercise.id,
      });
      const image = e.target.files[0];
      await handleCustomImageUpload(uidRef.current, image, 0, exercise);
    }
  };

  const handleImage2Upload = async (e) => {
    if (e.target.files[0]) {
      events.logButtonClick({
        buttonName: "Image 2 Upload",
        pageName: "Exercises",
        tabName: exercise.id,
      });
      const image = e.target.files[0];
      handleCustomImageUpload(uidRef.current, image, 1, exercise);
    }
  };

  function getEquipmentString(equipment) {
    if (!equipment || !Array.isArray(equipment)) {
      return "None required";
    }
    return equipment.join(", ");
  }

  const hasFirstImage =
    exercise?.images &&
    exercise.images.length >= 1 &&
    exercise.images[0]?.length > 0;
  const hasSecondImage =
    exercise?.images &&
    exercise.images.length >= 2 &&
    exercise.images[1]?.length > 0;

  const renderImageUploadPlaceholder = (
    imageIndex,
    handleImageUpload,
    isUploading,
  ) => (
    <Paper
      elevation={2}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: 420,
        height: 350,
        margin: 3,
        borderRadius: 2,
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
        transition: "all 0.3s ease",
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          boxShadow: theme.shadows[4],
        },
        position: "relative",
      }}
    >
      {isUploading && (
        <Backdrop
          sx={{
            position: "absolute",
            zIndex: 1,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            borderRadius: 2,
          }}
          open={true}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress color="primary" />
            <Typography sx={{ mt: 2 }}>Uploading...</Typography>
          </Box>
        </Backdrop>
      )}
      <AddAPhotoIcon
        color="primary"
        style={{ fontSize: 48, marginBottom: 16 }}
      />
      <Typography variant="body1" align="center" style={{ marginBottom: 16 }}>
        No image uploaded
      </Typography>
      <label htmlFor={`image-upload-${imageIndex}`}>
        <input
          id={`image-upload-${imageIndex}`}
          hidden
          type="file"
          accept=".jpg,.jpeg,.png"
          onChange={handleImageUpload}
          disabled={isUploading}
        />
        <Button
          variant="contained"
          component="span"
          color="primary"
          disabled={isUploading}
          startIcon={
            isUploading ? <CircularProgress size={20} /> : <AddAPhotoIcon />
          }
          sx={{
            borderRadius: "24px",
            boxShadow: theme.shadows[2],
            padding: "8px 20px",
          }}
        >
          {isUploading ? "Uploading..." : "Add Image"}
        </Button>
      </label>
    </Paper>
  );

  return (
    <Box
      sx={{
        padding: "30px 40px",
        backgroundColor: NewColors.cardBackground,
        borderRadius: 2,
        boxShadow: "0 6px 20px rgba(0, 0, 0, 0.05)",
        maxWidth: "1600px",
        margin: "20px auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 3,
          mb: 4,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            color: theme.palette.text.primary,
          }}
        >
          {exercise.name}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<FitnessCenterIcon />}
          onClick={() => setExerciseDialogOpen(true)}
          sx={{
            borderRadius: "24px",
            px: 3,
            py: 1,
          }}
        >
          Edit Exercise
        </Button>
      </Box>

      <Box sx={{ mb: 6 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <Card elevation={2} sx={{ p: 3, borderRadius: 3, height: "100%" }}>
              <CardContent sx={{ p: 2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 3,
                    pb: 1,
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FitnessCenterIcon color="primary" /> Overview
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Main Muscle Groups
                    </Typography>
                    <Box
                      sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}
                    >
                      {(() => {
                        // Handle both data structures:
                        // 1. New structure: exercise.muscleGroups = ["abs", "chest"]
                        // 2. Old structure: exercise.mainMuscleGroups = [id1, id2] (string names)

                        let mainMuscleGroups = [];

                        if (
                          exercise.muscleGroups &&
                          Array.isArray(exercise.muscleGroups)
                        ) {
                          // New structure - muscle groups are already strings
                          mainMuscleGroups = exercise.muscleGroups.slice(0, 3); // Show first 3
                        } else if (
                          exercise.mainMuscleGroups &&
                          Array.isArray(exercise.mainMuscleGroups)
                        ) {
                          // Old structure - check if they're already strings or need mapping
                          mainMuscleGroups = exercise.mainMuscleGroups.slice(
                            0,
                            3,
                          );
                        }

                        if (mainMuscleGroups.length === 0) {
                          return (
                            <Chip
                              label="Unknown"
                              color="primary"
                              sx={{ borderRadius: 2 }}
                            />
                          );
                        }

                        return mainMuscleGroups.map((muscleGroup, index) => (
                          <Chip
                            key={index}
                            label={muscleGroup}
                            color="primary"
                            sx={{ borderRadius: 2 }}
                          />
                        ));
                      })()}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Secondary Muscle Groups
                    </Typography>
                    <Box
                      sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}
                    >
                      {(() => {
                        let secondaryMuscleGroups = [];

                        if (
                          exercise.secondaryMuscleGroups &&
                          Array.isArray(exercise.secondaryMuscleGroups)
                        ) {
                          secondaryMuscleGroups =
                            exercise.secondaryMuscleGroups.slice(0, 3);
                        }

                        if (secondaryMuscleGroups.length === 0) {
                          return (
                            <Chip
                              label="None"
                              variant="outlined"
                              sx={{ borderRadius: 2 }}
                            />
                          );
                        }

                        return secondaryMuscleGroups.map(
                          (muscleGroup, index) => (
                            <Chip
                              key={index}
                              label={muscleGroup}
                              variant="outlined"
                              sx={{ borderRadius: 2 }}
                            />
                          ),
                        );
                      })()}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Equipment
                    </Typography>
                    <Typography sx={{ mt: 1 }}>
                      {exercise.equipment && exercise.equipment.length > 0
                        ? getEquipmentString(exercise.equipment)
                        : "None required"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Difficulty
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <SignalCellularAltIcon
                        color={
                          exercise.difficulty === "beginner"
                            ? "success"
                            : exercise.difficulty === "intermediate"
                              ? "info"
                              : exercise.difficulty === "advanced"
                                ? "warning"
                                : "error"
                        }
                      />
                      <Typography sx={{ ml: 1 }}>
                        {exercise.difficulty || "Unknown"}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Training Plans
                    </Typography>
                    <Chip
                      icon={<PlaylistAddCheckIcon />}
                      label={
                        foundProtocols.length > 0
                          ? `${foundProtocols.length} Plans`
                          : "None"
                      }
                      variant="outlined"
                      color="info"
                      sx={{ mt: 1, borderRadius: 2 }}
                    />
                  </Grid> */}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {/* First Image */}
              {hasFirstImage ? (
                <ImageListItem
                  sx={{
                    width: 420,
                    height: 350,
                    backgroundColor: "white",
                    margin: 2,
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: theme.shadows[3],
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.01)",
                      boxShadow: theme.shadows[6],
                    },
                    position: "relative",
                  }}
                >
                  {(isUploadingImage[0] || isDeletingImage[0]) && (
                    <Backdrop
                      sx={{
                        position: "absolute",
                        zIndex: 2,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        borderRadius: 3,
                      }}
                      open={true}
                    >
                      <Box sx={{ textAlign: "center", color: "white" }}>
                        <CircularProgress color="inherit" />
                        <Typography sx={{ mt: 2 }}>
                          {isDeletingImage[0] ? "Deleting..." : "Updating..."}
                        </Typography>
                      </Box>
                    </Backdrop>
                  )}
                  <img
                    src={exercise.images[0]}
                    alt={exercise.name}
                    style={{ height: "100%", objectFit: "cover" }}
                  />
                  <ImageListItemBar
                    sx={{
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
                    }}
                    actionIcon={
                      <Box sx={{ height: 25, marginRight: 2, display: "flex" }}>
                        <label>
                          <AddAPhotoIcon
                            sx={{ color: "white", cursor: "pointer", mr: 1 }}
                          />
                          <input
                            hidden={true}
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            onChange={handleImage1Upload}
                          />
                        </label>

                        <DeleteIcon
                          sx={{ color: "white", cursor: "pointer" }}
                          onClick={() => {
                            confirm({
                              title: "Remove the image?",
                              description:
                                "This will remove the image from the exercise. Continue?",
                            })
                              .then(async () => {
                                handleCustomImageDelete(
                                  uidRef.current,
                                  0,
                                  exercise,
                                );
                              })
                              .catch(() => {
                                // User cancelled the confirmation - no action needed
                              });
                          }}
                        />
                      </Box>
                    }
                  />
                </ImageListItem>
              ) : (
                renderImageUploadPlaceholder(
                  1,
                  handleImage1Upload,
                  isUploadingImage[0],
                )
              )}

              {/* Second Image */}
              {hasSecondImage ? (
                <ImageListItem
                  sx={{
                    width: 420,
                    height: 350,
                    backgroundColor: "white",
                    margin: 2,
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: theme.shadows[3],
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.01)",
                      boxShadow: theme.shadows[6],
                    },
                    position: "relative",
                  }}
                >
                  {(isUploadingImage[1] || isDeletingImage[1]) && (
                    <Backdrop
                      sx={{
                        position: "absolute",
                        zIndex: 2,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        borderRadius: 3,
                      }}
                      open={true}
                    >
                      <Box sx={{ textAlign: "center", color: "white" }}>
                        <CircularProgress color="inherit" />
                        <Typography sx={{ mt: 2 }}>
                          {isDeletingImage[1] ? "Deleting..." : "Updating..."}
                        </Typography>
                      </Box>
                    </Backdrop>
                  )}
                  <img
                    src={exercise.images[1]}
                    alt={exercise.name}
                    style={{ height: "100%", objectFit: "cover" }}
                  />
                  <ImageListItemBar
                    sx={{
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
                    }}
                    actionIcon={
                      <Box sx={{ height: 25, marginRight: 2, display: "flex" }}>
                        <label>
                          <AddAPhotoIcon
                            sx={{ color: "white", cursor: "pointer", mr: 1 }}
                          />
                          <input
                            hidden={true}
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            onChange={handleImage2Upload}
                          />
                        </label>

                        <DeleteIcon
                          sx={{ color: "white", cursor: "pointer" }}
                          onClick={() => {
                            confirm({
                              title: "Remove the image?",
                              description:
                                "This will remove the image from the exercise. Continue?",
                            })
                              .then(async () => {
                                handleCustomImageDelete(
                                  uidRef.current,
                                  1,
                                  exercise,
                                );
                              })
                              .catch(() => {
                                // User cancelled the confirmation - no action needed
                              });
                          }}
                        />
                      </Box>
                    }
                  />
                </ImageListItem>
              ) : (
                renderImageUploadPlaceholder(
                  2,
                  handleImage2Upload,
                  isUploadingImage[1],
                )
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mb: 5, mt: 2 }} />

      <Card elevation={2} sx={{ mb: 5, borderRadius: 3, p: 3 }}>
        <CardContent>
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
              pb: 1,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
            }}
          >
            <DescriptionIcon color="primary" /> Description
          </Typography>
          <NewLineTypography
            text={exercise.description || "No description provided."}
            sx={{ mt: 2, lineHeight: 1.7 }}
          />
        </CardContent>
      </Card>

      <Card elevation={2} sx={{ mb: 5, borderRadius: 3, p: 3 }}>
        <CardContent>
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
              pb: 1,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
            }}
          >
            <PlaylistAddCheckIcon color="primary" /> Instructions
          </Typography>
          <NewLineTypography
            text={exercise.instructions || "No instructions provided."}
            sx={{ mt: 2, lineHeight: 1.7 }}
          />
        </CardContent>
      </Card>

      <Card elevation={2} sx={{ borderRadius: 3, p: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                pb: 1,
                borderBottom: `2px solid ${theme.palette.primary.main}`,
              }}
            >
              <VideocamIcon color="primary" /> Video Instructions
            </Typography>

            <Button
              variant="contained"
              color="primary"
              disabled={isAddingVideo}
              startIcon={
                isAddingVideo ? (
                  <CircularProgress size={20} />
                ) : (
                  <VideocamIcon />
                )
              }
              onClick={() => {
                setAddVideoURLOpen(true);
                events.logButtonClick({
                  buttonName: "Add Video",
                  pageName: "Exercises",
                  tabName: exercise.id,
                });
              }}
              sx={{
                borderRadius: "24px",
                boxShadow: theme.shadows[2],
                px: 3,
              }}
            >
              {isAddingVideo ? "Adding Video..." : "Add Video"}
            </Button>
          </Box>

          {exercise.videos && exercise.videos.length > 0 ? (
            <Grid container spacing={4} sx={{ mt: 2 }}>
              {exercise.videos.map((e) => {
                let finalURL = e;
                if (e?.includes?.("youtube.com/shorts")) {
                  finalURL = e.split("?")[0].replace("shorts/", "watch?v=");
                }

                return (
                  <Grid
                    item
                    xs={12}
                    key={e}
                    sx={{
                      mt: 3,
                      borderRadius: 3,
                      overflow: "hidden",
                      boxShadow: theme.shadows[1],
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      p: 3,
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: theme.shadows[2],
                      }}
                    >
                      <ReactPlayer
                        width="100%"
                        height="450px"
                        url={finalURL}
                        controls={true}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 2,
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={isDeletingVideo[e]}
                        startIcon={
                          isDeletingVideo[e] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DeleteIcon />
                          )
                        }
                        onClick={async () => {
                          await deleteVideoFromExercise(e);
                        }}
                        sx={{
                          borderRadius: "24px",
                          mt: 2,
                        }}
                      >
                        {isDeletingVideo[e] ? "Deleting..." : "Delete Video"}
                      </Button>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                p: 6,
                mt: 3,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                borderRadius: 3,
                border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              <VideocamIcon
                sx={{
                  fontSize: 60,
                  color: alpha(theme.palette.text.secondary, 0.6),
                  mb: 2,
                }}
              />
              <Typography variant="body1" color="textSecondary">
                No videos have been added yet
              </Typography>
              <Button
                variant="contained"
                color="primary"
                disabled={isAddingVideo}
                startIcon={
                  isAddingVideo ? (
                    <CircularProgress size={20} />
                  ) : (
                    <VideocamIcon />
                  )
                }
                onClick={() => {
                  setAddVideoURLOpen(true);
                  events.logButtonClick({
                    buttonName: "Add Video",
                    pageName: "Exercises",
                    tabName: exercise.id,
                  });
                }}
                sx={{
                  mt: 3,
                  borderRadius: "24px",
                }}
              >
                {isAddingVideo ? "Adding Video..." : "Add Your First Video"}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {addVideoURLOpen && (
        <AddVideoDialog
          open={addVideoURLOpen}
          exercise={exercise}
          onDialogClosed={async (videoURL) => {
            if (videoURL && videoURL.length > 0) {
              await addVideoToExercise(videoURL);
            }
            setAddVideoURLOpen(false);
          }}
        />
      )}

      {exerciseDialogOpen && (
        <ExerciseDialog
          open={exerciseDialogOpen}
          exercise={exercise}
          onDialogClosed={() => setExerciseDialogOpen(false)}
        />
      )}
    </Box>
  );
};

export default ExerciseDetails;
